import { Link } from "react-router-dom";
import MarketingShell from "@/components/marketing/MarketingShell";
import { useProductName } from "@/context/BrandContext";
import { AGENT_PAGE_STYLES } from "@/components/marketing/AgentFlowDiagram";
import { ArrowRight } from "lucide-react";

const SIBLINGS = [
  { to: "/agents/voice", label: "Voice Agents", key: "voice" },
  { to: "/agents/ai", label: "AI Agents", key: "ai" },
  { to: "/agents/hybrid", label: "Hybrid Agents", key: "hybrid" },
];

export default function AgentExplainerLayout({
  active,
  eyebrow,
  title,
  lead,
  heroVisual,
  children,
  testId,
}) {
  const productName = useProductName();
  const name = productName || "our platform";

  return (
    <MarketingShell active="agents">
      <style>{AGENT_PAGE_STYLES}</style>
      <div data-testid={testId}>
        <section className="relative overflow-hidden border-b border-border">
          <div
            className="pointer-events-none absolute -top-32 right-0 h-[28rem] w-[28rem] rounded-full blur-3xl"
            style={{ background: "radial-gradient(circle, hsl(var(--primary) / 0.14), transparent 65%)" }}
          />
          <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-6 pb-14 pt-14 lg:grid-cols-2 lg:gap-14 lg:pt-16">
            <div className="m-rise max-w-xl">
              <span className="font-mono text-xs uppercase tracking-[0.22em] text-primary">{eyebrow}</span>
              <h1 className="disp mt-4 text-4xl font-extrabold leading-[1.08] tracking-tight sm:text-5xl">
                {title}
              </h1>
              <p className="mt-5 text-lg text-muted-foreground">{typeof lead === "function" ? lead(name) : lead}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/signup"
                  className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
                >
                  Get started <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/features"
                  className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm font-semibold transition-colors hover:bg-muted"
                >
                  See features
                </Link>
              </div>
            </div>
            <div className="m-rise m-float" style={{ animationDelay: "0.12s" }}>
              {heroVisual}
            </div>
          </div>
        </section>

        <nav
          className="border-b border-border bg-muted/30"
          aria-label="Agent types"
          data-testid="agents-subnav"
        >
          <div className="mx-auto flex max-w-7xl gap-1 overflow-x-auto px-4 py-2 sm:px-6">
            {SIBLINGS.map((s) => {
              const hot = s.key === active;
              return (
                <Link
                  key={s.to}
                  to={s.to}
                  className={`shrink-0 rounded-full px-4 py-2 text-sm transition-colors ${
                    hot
                      ? "bg-primary font-semibold text-primary-foreground"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  }`}
                >
                  {s.label}
                </Link>
              );
            })}
          </div>
        </nav>

        <div className="mx-auto max-w-7xl space-y-16 px-6 py-14 sm:space-y-20 sm:py-16">
          {children}
        </div>

        <section className="border-t border-border bg-muted/25">
          <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 px-6 py-12 sm:flex-row sm:items-center">
            <div>
              <h2 className="disp text-2xl font-bold tracking-tight">Ready to design a campaign?</h2>
              <p className="mt-2 max-w-md text-sm text-muted-foreground">
                Build Voice, AI, or Hybrid flows in {name} and launch on your VICIdial or SIP stack.
              </p>
            </div>
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground"
            >
              Create account <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      </div>
    </MarketingShell>
  );
}

export function Section({ eyebrow, title, children, testId }) {
  return (
    <section className="m-rise" data-testid={testId}>
      {eyebrow && (
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-primary">{eyebrow}</p>
      )}
      <h2 className="disp mt-2 text-2xl font-bold tracking-tight sm:text-3xl">{title}</h2>
      <div className="mt-6">{children}</div>
    </section>
  );
}

export function PointList({ items }) {
  return (
    <ul className="space-y-3">
      {items.map((item) => (
        <li key={item.title || item} className="flex gap-3">
          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
          <div>
            {item.title ? (
              <>
                <p className="font-semibold">{item.title}</p>
                <p className="mt-0.5 text-sm text-muted-foreground">{item.body}</p>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">{item}</p>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}
